<template>
  <tr>
    <template v-if="header">
      <td class="nosort">
        <div class="account-name">Account Name</div>
      </td>
      <td class="violations nosort">Violations</td>
      <td class="nosort">Carrier</td>
      <td class="nosort">Policy</td>
      <td class="nosort">Coverage Type</td>
      <td class="nosort tw:text-left">Premium</td>
      <td class="nosort tw:text-center">PE Flag</td>
      <td class="nosort number"><div class="tw:inline-block tw:text-center">Agg Limit</div></td>
      <td class="nosort number"><div class="tw:inline-block tw:text-center">Occ Limit</div></td>
      <td class="nosort number">Attachment</td>
      <td class="nosort number">RET</td>
      <td class="nosort number">Total Layer Limit</td>
      <td class="nosort tw:text-center">Action</td>
    </template>
    <template v-else-if="violation">
      <td class="account-name">
        <div class="account-name">
          <router-link
            :to="`/account/${violation.accountID}/policy-detail`"
            data-test="account-name"
            :title="violation.accountName"
          >
            {{ filters.truncate(violation.accountName, 30) }}
          </router-link>
        </div>
      </td>
      <td class="violations">
        <div class="rule-container tw:inline-flex" v-for="(rule) in violatedRules(violation)" :key="rule.key">
          <v-tooltip location="bottom" v-if="rule.violated">
            <template v-slot:activator="{ props }">
              <span
                v-bind="props"
                class="tw:mr-0.5 tw:flex tw:h-5 tw:w-5 tw:scale-[85%] tw:items-center tw:justify-center tw:rounded-full tw:bg-primary tw:text-[10pt] tw:font-bold tw:text-white">
                <span class="tw:h-fit tw:w-fit tw:font-mono">{{ rule.value }} </span>
              </span>
            </template>
            <h4>Rule</h4>
            <p>{{ rule.desc }}</p>
            <h4>Resolution</h4>
            <p>{{ rule.resolution }}</p>
          </v-tooltip>
        </div>
      </td>
      <td>
        <a v-if="violation.insurerGroupID" :href="`/carrier/${violation.insurerGroupID}/overview`">{{
          violation.insurerGroup ?? '--'
        }}</a>
        <span v-else>{{ violation.insurerGroup ?? '--' }}</span>
      </td>
      <td>
        <div v-if="violation.hasDocuments">
          <div v-if="violation.hasDocuments" class="tw:flex tw:items-center">
            {{ violation.mainPolicyNumber ?? '--' }}

            <DocumentModal>
              <template v-slot:activator="{ open }">
                <button
                  class="tw:btn-xs"
                  @click="open(violation.accountID, violation?.policyID, violation?.mainPolicyNumber)">
                  <FileText :size="16" class="hyperlink-icon tw:stroke-primary" />
                </button>
              </template>
            </DocumentModal>
          </div>
          <div class="tw:text-xs" :title="violation.description">{{ filters.truncate(violation.description) }}</div>
        </div>

        <div v-show="!violation.hasDocuments">
          <div class="tw:flex tw:items-center">
            {{ violation.mainPolicyNumber ?? '--' }}
            <div class="tw:group tw:flex tw:items-center">
              <DocumentModal ref="documentUpload">
                <template v-slot:activator="{ open }">
                  <button
                    class="tw:btn-xs tw:invisible tw:group-hover:visible"
                    @click="open(Number(violation.accountID), Number(violation.policyID), violation?.mainPolicyNumber)">
                    <FilePlus2 :size="16" class="tw:text-red-500" />
                  </button>
                </template>
              </DocumentModal>
            </div>
          </div>

          <div class="tw:text-xs" :title="violation.description">{{ filters.truncate(violation.description) }}</div>
        </div>
      </td>
      <td class="tw:text-left">{{ violation.coverageType ?? '--' }}</td>
      <td class="tw:text-left">
        <span>{{ filters.compactMoney(violation.policyPremium) || '--' }}</span>
        <span> ({{ violation.billMode ?? '--' }})</span>
      </td>
      <td class="tw:text-center">{{ violation.peFlag ?? '--' }}</td>
      <td class="number">{{ filters.compactMoney(violation.aggregateLimit) ?? '--' }}</td>
      <td class="number">{{ filters.compactMoney(violation.occurrenceLimit) ?? '--' }}</td>
      <td class="number">{{ filters.compactMoney(parseFloat(violation.attachmentPointValue)) ?? '--' }}</td>
      <td class="number">{{ filters.compactMoney(violation.retention) ?? '--' }}</td>
      <td class="number">{{ filters.compactMoney(parseFloat(violation.quotashareTotal)) ?? '--' }}</td>
      <td class="tw:text-center"><MenuDropdown :items="actionMenuItems" /></td>
    </template>
  </tr>
</template>

<script lang="ts">
import { DataQualityPolicy, DataQualityRule } from '@/types/DataQuality';
import { filters } from '@/utilities/Filters';
import MenuDropdown from '@/components/v2/MenuDropdown.vue';
import { FilePlus2, FileText } from 'lucide-vue-next';
import DocumentModal from '@/components/v2/document/DocumentModal.vue';

export default {
  name: 'DataQualityPolicyRow',
  props: {
    header: Boolean,
    violation: Object as () => DataQualityPolicy,
    rules: Array as () => Readonly<DataQualityRule[]>,
  },
  data: () => ({
    filters,
    showDocumentModal: false,
  }),
  methods: {
    editPolicy() {
      if (!this.violation) return;
      this.$emit('edit-policy', this.violation.policyID, this.violation.mainPolicyNumber);
    },
    violatedRules(policy: DataQualityPolicy): (DataQualityRule & { violated: boolean })[] {
      if (!this.rules) return [];
      return this.rules.map((r, i) => ({
        ...r,
        violated: Reflect.get(policy, 'rule' + (i + 1)),
      })) as (DataQualityRule & { violated: boolean })[];
    },
  },
  computed: {
    actionMenuItems() {
      const actions = [{ label: 'Edit Policy', handler: this.editPolicy }];
      if (this.violation?.hasDocuments) {
        actions.push({
          label: 'View Documents',
          handler: () => {
            this.showDocumentModal = true;
          },
        });
      }
      actions.push({
        label: 'Upload Documents',
        handler: () =>
          (this.$refs.documentUpload as InstanceType<typeof DocumentModal>).open(
            Number(this.violation?.accountID),
            Number(this.violation?.policyID),
            this.violation?.mainPolicyNumber || '',
          ),
      });
      return actions;
    },
  },
  components: { DocumentModal, MenuDropdown, FileText, FilePlus2 },
};
</script>

<style scoped>
.account-name {
  width: 250px;
  overflow: hidden;
}
</style>
